import React, { FC, useEffect, useRef, useState } from 'react'

// Plugins
import classnames from 'classnames'
// Components
import { Video } from 'components/shared'

// Styles
import { Box, Button, Grid, makeStyles, Theme, Typography } from '@material-ui/core'

// Types
import { GameFlowSteps, IUser } from 'types'
import { IDemo } from 'types'

// Helpers
import { GameFlowStepsConfig } from 'helpers/steps'
import { createInteraction, Demos, EventReplays, Sessions } from 'helpers'

// Images
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import { useAppState } from 'providers'

interface DemoProps {
  demo: IDemo
  setScene: (sceneTo: GameFlowSteps) => void
  user?: IUser | null
  isSession: boolean
  setIsSession: (bool: boolean) => void
  isEventReplay: boolean
  setIsEventReplay: (bool: boolean) => void
}

export const Demo: FC<DemoProps> = ({
  demo,
  setScene,
  user,
  isSession,
  setIsSession,
  isEventReplay,
  setIsEventReplay
}) => {
  const classes = useStyles()
  const {
    appState: { eventStage, userInitiatedMute }
  } = useAppState()
  const [renderDemo, setRenderDemo] = useState<boolean>(false)
  const [autoPlay, setAutoPlay] = useState<boolean>(Boolean(demo.autoplay))
  const [demoHeight, setDemoHeight] = useState<number>(0)
  const containerRef = useRef<any>(null)

  const [playTime, setPlayTime] = useState<number>(0)

  const sendPlayTime = () => {
    if (playTime > 0) {
      createInteraction(
        demo?.id ?? '',
        user?.email ?? '',
        isEventReplay ? 'eventReplay' : isSession ? 'sessions' : 'demo',
        'watchTime',
        eventStage,
        playTime + ''
      )
    }
    setPlayTime(0)
  }

  useEffect(() => {
    // if the desired demo switches without unmount/mount from here,
    // reset this component manually to start at the beginning of the new demo
    setAutoPlay(Boolean(demo.autoplay))
    containerRef?.current?.scrollTo?.(0, 0)

    setTimeout(() => {
      setRenderDemo(true)
    }, GameFlowStepsConfig[GameFlowSteps.Demo].animation.time)

    window.addEventListener('beforeunload', sendPlayTime)
    return () => {
      window.removeEventListener('beforeunload', sendPlayTime)
    }
    // eslint-disable-next-line
  }, [demo])

  function setDemoContainerHeight(height) {
    if (height && height !== demoHeight) {
      setDemoHeight(height)
    }
  }

  const actionClicked = (action, param?) => {
    switch (action) {
      case 'explore':
        sendPlayTime()
        setScene(GameFlowSteps.BackToExplore)
        break
      case 'sessions':
        sendPlayTime()
        setScene(GameFlowSteps.BackToSessions)
        break
      case 'connect':
        window.postMessage(JSON.stringify({ command: 'connect', param: param }), '*')
        break
      case 'demo':
        // this will trigger the useEffect(() => ..., [demo]) to trigger which will reset the compoennt
        sendPlayTime()
        window.postMessage(JSON.stringify({ command: 'demos', param: param }), '*')
        break
      case 'session':
        // this will trigger the useEffect(() => ..., [demo]) to trigger which will reset the compoennt
        sendPlayTime()
        window.postMessage(JSON.stringify({ command: 'sessions', param: param }), '*')
        break
      case 'eventReplay':
        sendPlayTime()
        window.postMessage(JSON.stringify({ command: 'eventReplay', param: param }), '*')
    }
  }

  const nextDemo = () => {
    // demo number params in babylon are 1-10 instead of base 0 so we need to add one to the index
    if (isSession) {
      const nextDemoIndex = Sessions.findIndex(d => d.id === demo.end?.nextDemoId)

      actionClicked('session', `${nextDemoIndex + 1}`)
    } else if (isEventReplay) {
      const nextDemoIndex = EventReplays.findIndex(d => d.id === demo.end?.nextDemoId)
      actionClicked('eventReplay', `${nextDemoIndex + 1}`)
    } else {
      const nextDemoIndex = Demos.findIndex(d => d.id === demo.end?.nextDemoId)
      actionClicked('demo', `${nextDemoIndex + 1}`)
    }
  }

  const unmuteAudio = () => {
    if (!userInitiatedMute) {
      const ifx: HTMLIFrameElement = document.getElementById('ifx') as HTMLIFrameElement
      const volume = localStorage.getItem('volume')
      //@ts-ignore
      if (ifx?.contentWindow?.MVRK?.SceneSoundSystem?.MUSIC?.setGroupVolume) {
        //@ts-ignore
        ifx.contentWindow.MVRK.SceneSoundSystem.MUSIC.setGroupVolume(+volume)
      }
    }
  }

  return (
    <div className={classes.root}>
      {renderDemo && (
        <div className={classnames(classes.transition, classes.mainContainer)} ref={containerRef}>
          <Grid container direction='row' className={classes.contentContainer}>
            <Grid item xs={12} md={9} className={classnames(classes.demoContainer)}>
              <Video
                videoHeightUpdated={setDemoContainerHeight}
                tracks={demo.tracks || []}
                videoSrc={demo.video || ''}
                posterSrc={demo.poster || ''}
                autoPlay={autoPlay}
                disableContextMenu={true}
                disableControls={false}
                setPlayTime={setPlayTime}
              />
            </Grid>
            <Grid
              xs={12}
              md={3}
              className={classes.endContainer}
              container
              item
              direction='column'
              justify='space-between'
              style={{ alignSelf: 'stretch', height: demoHeight || '100%' }}
            >
              <Grid className={classes.endContentContainer} item>
                {demo?.end?.header && (
                  <Typography variant='h4' component='h4' paragraph className={classes.endHeader}>
                    <span dangerouslySetInnerHTML={{ __html: demo?.end?.header }} />
                  </Typography>
                )}
                {demo?.end?.body && (
                  <Typography component='span' dangerouslySetInnerHTML={{ __html: demo?.end?.body }} />
                )}
              </Grid>
              <Grid item container alignItems='flex-end' className={classes.endContentContainer}>
                {demo?.end?.nextDemoId && (
                  <div className={classes.endContentThumbnail}>
                    <Grid item xs={12} container direction={'row'} alignItems='flex-end'>
                      <Grid item xs={6}>
                        <Typography className={classes.nextUseCase} component='span'>
                          {isEventReplay ? 'Next video' : 'Next use case'}
                        </Typography>
                      </Grid>
                      {!isEventReplay && (
                        <Grid
                          item
                          xs={6}
                          className={classes.viewAllDemos}
                          onClick={() => {
                            unmuteAudio()
                            actionClicked(isSession ? 'sessions' : 'explore')
                          }}
                        >
                          <Typography component='span' className={classes.demoLink}>
                            View all
                          </Typography>
                          <span>
                            <ArrowBackIcon fontSize='small' classes={{ root: classes.forwardArrow }} />
                          </span>
                        </Grid>
                      )}
                    </Grid>
                    <Box className={classes.thumbnailContainer} flexGrow={1}>
                      <figure
                        className={classes.thumbnail}
                        onClick={() => {
                          unmuteAudio()
                          nextDemo()
                        }}
                      >
                        <img src={demo?.end?.nextDemoThumbnail} alt='Demo thumbnail' />
                        <figcaption>
                          <Typography
                            variant='h6'
                            className={classnames(classes.subHeader)}
                            dangerouslySetInnerHTML={{ __html: demo?.end?.nextDemoText || '' }}
                          />
                        </figcaption>
                      </figure>
                    </Box>
                  </div>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Box display='flex' className={classes.contentActionBox}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                sendPlayTime()
                if (isSession) {
                  setScene(GameFlowSteps.BackToSessions)
                  setIsSession(false)
                } else if (isEventReplay) {
                  const prevIndex = EventReplays.findIndex(d => d.id === demo.end?.previousDemoId)
                  actionClicked('eventReplay', `${prevIndex + 1}`)
                } else {
                  setScene(GameFlowSteps.BackToExplore)
                }
              }}
            >
              Back
            </Button>

            {!isEventReplay && (
              <Button startIcon={<MailOutlineIcon />} onClick={() => actionClicked('connect')}>
                Send a message
              </Button>
            )}
          </Box>
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 'calc(100% - 64px)',
    position: 'absolute',
    top: 115,
    bottom: 60,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'transparent',
    color: '#000',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      top: 60
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  mainContainer: {
    width: '100%',
    maxHeight: '100%',
    maxWidth: 'calc(100vh * 1.75 - 175px)',
    minWidth: '900px',
    padding: '2rem 5rem',

    [theme.breakpoints.down('lg')]: {
      paddingRight: '3rem',
      paddingLeft: '3rem'
    },

    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      maxWidth: '100%',
      height: '100%',
      overflowY: 'auto',
      paddingRight: '5rem',
      paddingLeft: '5rem'
    }
  },
  demoContainer: {
    paddingRight: '5rem',
    [theme.breakpoints.down('lg')]: {
      paddingRight: '3rem'
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      paddingBottom: '1rem'
    }
  },
  maxHeightWidth: {
    width: '100%',
    height: '100%'
  },
  containedImage: {
    width: '100%'
  },
  columnCenter: {
    justifyContent: 'center',
    flexDirection: 'column'
  },
  thumbnailContainer: {
    paddingTop: 10,
    minHeight: 120,
    width: '100%'
  },
  thumbnailHeaderText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 16
    }
  },
  thumbnailPlayButton: {
    marginLeft: '10px'
  },
  thumbnail: {
    cursor: 'pointer',
    position: 'relative',

    '& > img': {
      width: '100%',
      marginBottom: '-5px',
      [theme.breakpoints.down('md')]: {
        marginBottom: '-2px'
      }
    },

    '& > figcaption': {
      bottom: 0,
      color: theme.palette.common.white,
      padding: theme.spacing(2),
      position: 'absolute',
      textShadow: `1px 1px 2px ${theme.palette.common.black}`,
      [theme.breakpoints.down('sm')]: {
        padding: 5
      },
      '& h2': {
        [theme.breakpoints.down('md')]: {
          fontSize: '16px !important'
        },
        [theme.breakpoints.down('sm')]: {
          fontSize: '20px !important'
        }
      }
    }
  },
  viewAllDemos: {
    textAlign: 'right',
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  forwardArrow: {
    WebkitTransform: 'rotate(180deg)',
    transform: 'rotate(180deg)',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  demoLink: {
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontFamily: 'Verizon-Bold',
    paddingRight: 2
  },
  nextUseCase: {
    fontSize: '0.875rem',
    fontFamily: 'Verizon-Bold',
    marginTop: 8
  },
  inlineDisplay: {
    display: 'inline-flex'
  },
  body: {
    paddingTop: 5,
    [theme.breakpoints.only('lg')]: {
      fontSize: '16px !important'
    },
    [theme.breakpoints.only('md')]: {
      paddingTop: 0,
      fontSize: '0.875rem !important'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem !important',
      overflowY: 'auto'
    }
  },
  itemsContainer: {
    paddingBottom: '1rem'
  },
  subHeader: {
    color: '#fff'
  },
  headerList: {
    paddingBottom: '2rem'
  },
  subLogo: {
    height: '20px',
    marginRight: '1.5rem'
  },
  paragraph: {
    fontSize: '22px',
    lineHeight: '1.2'
  },
  centerContent: {
    padding: '0 6rem 0 0',
    height: '100%',
    alignContent: 'center',
    justifyContent: 'center'
  },
  endContainer: {
    [theme.breakpoints.down('sm')]: {
      height: '100% !important'
    }
  },
  extraPaddingBottom: {
    paddingBottom: 'calc(6rem + 60px)'
  },
  contentContainer: {
    alignItems: 'flex-start'
  },
  endHeader: {
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5rem',
      lineHeight: 'calc(1.5rem * .95)'
    }
  },
  endContentContainer: {
    maxWidth: '100%',
    height: '50%',
    overflowY: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.down('sm')]: {
      minHeight: 0
    },
    '&:nth-child(2)': {
      alignContent: 'flex-end'
    },
    '& .MuiGrid-item': {
      padding: '0',

      [theme.breakpoints.down('sm')]: {
        padding: '0 0 5px'
      }
    }
  },
  endContentThumbnail: {
    borderTop: '1px solid #DBDBDB',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  contentActionBox: {
    paddingTop: '1rem',

    '& .MuiButton-root': {
      marginRight: theme.spacing(4),
      marginTop: 0,
      marginBottom: 0
    },

    [theme.breakpoints.down('sm')]: {
      paddingTop: '0.875rem'
    }
  },
  transition: {
    opacity: 1,
    animationName: '$fadeInOpacity',
    animationIterationCount: 1,
    animationTimingFunction: 'ease-in',
    animationDuration: '0.75s'
  },
  '@keyframes fadeInOpacity': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    }
  }
}))
